import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import EntryCodeModal from '../../components/shared/EntryCodeModal';
import { PatientRegistrationForm } from './PatientRegistrationForm';
import ErrorPage from '../ErrorPage';
import { getRegistrationFormBySession } from '../../api/admin';
import './PatientRegistration.css';

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface PatientRegistrationProps {}

interface TokenValidationData {
  patient_id: string;
  patient_email?: string; // Masked email for display
  expires_at: string;
  entry_attempts: number;
  max_attempts: number;
  requires_entry_code: boolean;
  language?: string; // Add language field
}

interface PatientAccessData {
  access_granted: boolean;
  patient_id: string;
  patient_data: any;
  session_id: string;
  form_progress?: Record<string, boolean>;
  language?: string;
}

const PatientRegistration: React.FC<PatientRegistrationProps> = () => {
  const { registrationToken } = useParams<{ registrationToken: string }>();
  
  const [isLoading, setIsLoading] = useState(true);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [tokenData, setTokenData] = useState<TokenValidationData | null>(null);
  const [patientAccess, setPatientAccess] = useState<PatientAccessData | null>(null);
  const [error, setError] = useState<{ type: string; message: string } | null>(null);

  useEffect(() => {
    if (!registrationToken) {
      setError({ type: 'invalid_link', message: 'Invalid registration link' });
      setIsLoading(false);
      return;
    }

    // Validate registration token format (similar to questionnaire tokens)
    if (!/^[A-Z0-9]{8,12}$/.test(registrationToken)) {
      setError({ type: 'invalid_link', message: 'Invalid link format' });
      setIsLoading(false);
      return;
    }

    accessRegistrationLink();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationToken]);

  const accessRegistrationLink = async () => {
    try {
      setIsLoading(true);
      
      // Call patient registration access endpoint
      const response = await fetch(`${API_BASE_URL}/api/patient-registration/${registrationToken}`, {
        credentials: 'include'
      });
      const result = await response.json();

      if (response.ok) {
        // Check access type
        if (result.data.access_type === 'form') {
          // Valid session exists - direct access to registration form
          setPatientAccess({
            access_granted: true,
            patient_id: result.data.patient_id,
            patient_data: result.data.patient_data,
            session_id: result.data.session_id,
            form_progress: result.data.form_progress || {}
          });
        } else if (result.data.access_type === 'otp_required') {
          // OTP required - show entry modal
          setTokenData({
            patient_id: result.data.patient_id,
            expires_at: result.data.token_info?.link_expires_at || '',
            entry_attempts: result.data.token_info?.failed_attempts || 0,
            max_attempts: result.data.token_info?.max_attempts || 5,
            requires_entry_code: true,
            language: result.data.language || 'el' // Capture language from backend
          });
          setShowEntryModal(true);
        }
      } else {
        handleLinkAccessError(response.status, result.detail || result.message);
      }
    } catch (error) {
      console.error('Registration link access error:', error);
      setError({ 
        type: 'general', 
        message: 'The registration link is temporarily unavailable. Please contact your healthcare provider.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkAccessError = (status: number, message: string) => {
    if (status === 410) {
      setError({ type: 'expired_link', message: message || 'Registration link has expired' });
    } else if (status === 423) {
      setError({ type: 'locked_link', message: message || 'Registration link has been locked due to security reasons' });
    } else if (status === 404) {
      setError({ type: 'invalid_link', message: 'Registration link not found' });
    } else if (status === 429) {
      setError({ type: 'too_many_requests', message: 'Too many attempts. Please try again later.' });
    } else {
      setError({ type: 'general', message: message || 'Unable to access registration form' });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="registration-loading">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4 className="mt-3">Loading Registration Form...</h4>
          <p className="text-muted">Please wait while we prepare your registration form.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return <ErrorPage errorType={error.type as any} message={error.message} />;
  }

  // Entry code modal
  if (showEntryModal && tokenData) {
    return (
      <EntryCodeModal
        isOpen={true}
        displayToken={registrationToken!}
        type="registration"
        initialLanguage={tokenData.language as 'en' | 'el'}
        onSuccess={async (data) => {
          try {
            // After OTP verification, create session and get session data
            const sessionData = await getRegistrationFormBySession(data.session_id);
            

            
            setPatientAccess({
              access_granted: true,
              patient_id: sessionData.patient_id,
              patient_data: sessionData.patient_data,
              session_id: data.session_id,
              form_progress: sessionData.form_progress || {},
              language: sessionData.language
            });
            setShowEntryModal(false);
          } catch (error) {
            console.error('Failed to get session data:', error);
            setError({ type: 'general', message: 'Failed to access registration form. Please try again.' });
          }
        }}
        onError={(error) => {
          console.error('Entry code error:', error);
          setError({ type: 'general', message: error });
        }}
      />
    );
  }

  // Main registration form
  if (patientAccess?.access_granted) {
    return (
      <PatientRegistrationForm
        patientId={patientAccess.patient_id}
        initialData={patientAccess.patient_data}
        sessionId={patientAccess.session_id}
        registrationToken={registrationToken!}
        initialFormProgress={patientAccess.form_progress}
        initialLanguage={patientAccess.language}
      />
    );
  }

  // Fallback error
  return (
    <ErrorPage 
      errorType="general"
      message="Unable to access registration form. Please contact your healthcare provider."
    />
  );
};

export default PatientRegistration;